/**
 * Service centralisé pour l'authentification en mode iframe.
 *
 * Flow (modèle Keycloak-direct) :
 * 1. Le front (en iframe) envoie postMessage `smartrecord:requestToken` à la page parente.
 * 2. La page parente répond avec postMessage `smartrecord:token` contenant un token Keycloak.
 * 3. Le front STOCKE directement ce token Keycloak et l'envoie comme `Authorization: Bearer`
 *    sur tous les appels API ; le backend le valide via JWKS (plus d'échange contre un
 *    session_jwt interne). L'état `calendar_authorized` — qui était porté par l'ancien
 *    session_jwt — est désormais obtenu via GET /api/me.
 *
 * Utilisé par ProtectedRoute (auth initiale) et par l'intercepteur axios (refresh sur 401).
 */

import { setJWT } from '../utils/auth';

const PARENT_TOKEN_TIMEOUT_MS = 3000;
const DEFAULT_RETRIES = 3;
const DEFAULT_RETRY_DELAY_MS = 500;

/**
 * Détecte si la fenêtre courante s'exécute dans une iframe.
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

export interface IframeAuthResult {
  session_jwt: string;
  calendar_authorized: boolean;
}

interface RequestTokenOptions {
  retries?: number;
  retryDelayMs?: number;
  timeoutMs?: number;
}

/**
 * Envoie un postMessage `smartrecord:requestToken` à la page parente et attend
 * la réponse `smartrecord:token`. Retente jusqu'à `retries` fois en cas de timeout.
 *
 * targetOrigin = '*' : on ne connaît pas l'origine du parent, c'est à lui de filtrer
 * les origines qu'il accepte. Le token reçu est validé côté backend (POST /auth/iframe-token).
 */
export function requestTokenFromParent(options: RequestTokenOptions = {}): Promise<string> {
  const retries = options.retries ?? DEFAULT_RETRIES;
  const retryDelayMs = options.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;
  const timeoutMs = options.timeoutMs ?? PARENT_TOKEN_TIMEOUT_MS;

  const attempt = (remaining: number): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const timeout = setTimeout(() => {
        window.removeEventListener('message', handler);
        reject(new Error('Parent token timeout'));
      }, timeoutMs);

      function handler(event: MessageEvent) {
        const type = event.data?.type;
        if (type === 'smartrecord:token' && event.data.token) {
          clearTimeout(timeout);
          window.removeEventListener('message', handler);
          resolve(event.data.token);
        }
      }

      window.addEventListener('message', handler);

      if (window.parent && window.parent !== window) {
        try {
          window.parent.postMessage({ type: 'smartrecord:requestToken' }, '*');
        } catch (e) {
          /* postMessage may throw on cross-origin issues */
        }
      }
    }).catch((e) => {
      if (remaining > 0) {
        return new Promise<string>((resolve) =>
          setTimeout(() => resolve(attempt(remaining - 1)), retryDelayMs)
        );
      }
      throw e;
    });
  };

  return attempt(retries);
}

/**
 * Modèle Keycloak-direct : stocke le token Keycloak reçu du parent (il sera
 * envoyé comme Bearer sur tous les appels API, validé côté backend via JWKS),
 * puis récupère l'état `calendar_authorized` via GET /api/me (cet état n'est
 * plus porté par un token interne).
 *
 * NB : le nom `exchangeParentToken` est conservé pour ne pas toucher aux appelants.
 */
export async function exchangeParentToken(keycloakToken: string): Promise<IframeAuthResult> {
  // Stockage immédiat : les appels /api/me et suivants l'utiliseront comme Bearer.
  setJWT(keycloakToken);

  let calendar_authorized = false;
  try {
    const resp = await fetch('/api/me', {
      method: 'GET',
      headers: { Authorization: `Bearer ${keycloakToken}` },
      credentials: 'include',
    });
    if (resp.ok) {
      const me = await resp.json();
      calendar_authorized = !!me?.calendar_authorized;
    }
    // Un /api/me en échec (réseau, 5xx) ne doit pas casser l'auth : on laisse
    // calendar_authorized=false, la vérif calendrier sera refaite plus tard.
  } catch (e) {
    /* ignore : /api/me indisponible */
  }

  return { session_jwt: keycloakToken, calendar_authorized };
}

/**
 * Promesse partagée en cours pour éviter d'envoyer plusieurs flows d'auth concurrents
 * (ex : plusieurs requêtes API qui retournent 401 en même temps).
 */
let inflightAuthPromise: Promise<IframeAuthResult> | null = null;

/**
 * Authentifie le front en iframe : demande un token au parent puis l'échange contre un session_jwt.
 * Idempotent : les appels concurrents partagent la même promesse en vol.
 */
export function authenticateInIframe(options?: RequestTokenOptions): Promise<IframeAuthResult> {
  if (inflightAuthPromise) {
    return inflightAuthPromise;
  }
  inflightAuthPromise = (async () => {
    try {
      const token = await requestTokenFromParent(options);
      const result = await exchangeParentToken(token);
      return result;
    } finally {
      inflightAuthPromise = null;
    }
  })();
  return inflightAuthPromise;
}
